import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ContentEventBloc } from '@kbloc';
import { SocialChallengeBloc } from '@kf-sc';
import { CardBloc } from '@kp/shared/components/cards/card.bloc';
import { TitleService } from '@kservice';
import {
  CardRequestType,
  CardScreen,
  ContentEventCreatedResponse,
  ContentEventType,
  CustomCardCategory,
} from '@ktypes/enums';
import { SocialChallenge, SocialChallengeState, Status } from '@ktypes/models';
import { differenceInDays } from 'date-fns';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

export const BACKGROUND_COLORS = {
  upcoming: 'var(--secondary)',
  active: 'var(--primary)',
  ended: 'var(--greyMedium)',
};

@Component({
  selector: 'kp-screen-challenge-detail',
  templateUrl: './screen-challenge-detail.component.html',
  styleUrls: ['./screen-challenge-detail.component.scss'],
})
export class ScreenChallengeDetailComponent implements OnInit, OnDestroy {
  backgroundColor: string;
  socialChallenge: SocialChallenge;
  socialChallengeState = SocialChallengeState;
  cardRequestType = CardRequestType.AVAILABLE;
  cardScreen = CardScreen.challenges;
  customCardCategory = CustomCardCategory.challenges;
  daysToStart: number;
  loadingStatus = Status.starting;

  private _challengeId: string;
  private _destroy$ = new Subject<void>();
  private _title = 'Challenges';

  constructor(
    private _activatedRoute: ActivatedRoute,
    public cardBloc: CardBloc,
    public socialChallengeBloc: SocialChallengeBloc,
    private _contentEventBloc: ContentEventBloc,
    private _titleService: TitleService
  ) {}

  ngOnInit() {
    this._titleService.set(this._title);
    this._challengeId = this._activatedRoute.snapshot.paramMap.get('challengeId');
    this.socialChallengeBloc.getChallengeById(this._challengeId);

    this.socialChallengeBloc.currentChallenge$.pipe(takeUntil(this._destroy$)).subscribe((challenge) => {
      if (challenge) {
        this.socialChallenge = challenge;
        this._calculateChallengeInfo();
        // Both Available and Take Action Categories will be filtered by the presence of the domain key
        // Take Action: CardRequestType.DOMAIN_ACTION will pull tracked actions related to the challenge
        this.cardBloc.refresh(
          [CardRequestType.DOMAIN_AVAILABLE, CardRequestType.DOMAIN_ACTION],
          null,
          this.socialChallenge.domain?.key
        );
      }
    });

    this._contentEventBloc.latestContentEventResponse$
      .pipe(
        filter((response: ContentEventCreatedResponse) => response?.eventInfo?.challengeId != null),
        takeUntil(this._destroy$)
      )
      .subscribe((response: ContentEventCreatedResponse) => {
        // on content event completion, update the challenge (success or fail)
        // but only if there is a challenge ID to update
        this.socialChallengeBloc.getChallengeById(response?.eventInfo?.challengeId);
      });
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  joinChallenge(challenge: SocialChallenge) {
    this.socialChallengeBloc.toggleEnrolledStateLocally(challenge.id);
    this._contentEventBloc.createContentEvent(challenge.contentId, ContentEventType.challenge_join, {
      challengeId: challenge.id,
    });
  }

  private _calculateChallengeInfo() {
    switch (this.socialChallenge.status.state) {
      // using switch to allow for additional conditions to be added later
      case SocialChallengeState.upcoming: {
        const difference = differenceInDays(this.socialChallenge.startDate, new Date());
        this.daysToStart = difference < 0 ? 0 : difference;
        break;
      }
      case SocialChallengeState.active:
        break;
      case SocialChallengeState.ended:
        break;
    }
    this.backgroundColor = BACKGROUND_COLORS[this.socialChallenge.status.state];
  }
}
